import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import mermaid from 'mermaid';
import Panzoom, { PanZoom } from 'panzoom';
import { Subscription } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { getIdMapping } from 'src/app/STORE/actions/screens.actions';
import { allIdMaps } from 'src/app/STORE/selectors/screens.selector';

@Component({
  selector: 'vb-wf-mermaid-diagram',
  templateUrl: './wf-mermaid-diagram.component.html',
  styleUrls: ['./wf-mermaid-diagram.component.scss'],
  standalone: false  //NOSONAR
})
export class WfMermaidDiagramComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private store = inject(Store);

  @Input() wfjson: any = null;
  @Input() roles: any = null;
  @Input('fullScreen') isFullScreen = false;
  @Output() public closeVis = new EventEmitter();
  @Input() appMetaInfo = null;
  @Input() renderInsideModernizeMenu = false;
  @Input() generationId: string | null = null;
  @Input() status: any = {};
  @Input() initialStatus: string | null = null;
  @Input() endStatus: string[] = [];
  @Output() isMermaidRendered = new EventEmitter();
  @ViewChild('mermaid') public mermaidDiv;
  statusMap: any = {};
  actorMap: any = {};
  actionMap: any = {};
  currentAlphabet: string;
  workflowString = '';
  zoomElement: PanZoom;
  svgCode: string | null = null;
  fieldIdMap: any;
  tableIdMap: any;
  idsSub: Subscription;
  private isViewInitialized = false;
  private previousWfjsonString = '';
  @Input()renderVisualizerOnly: boolean = false;

  getNextAlphabet() {
    const alphabet = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z'
    ];
    const nextAlphabet = alphabet[alphabet.indexOf(this.currentAlphabet) + 1];
    this.currentAlphabet = nextAlphabet;
    return nextAlphabet;
  }
  clearExistingMap(){
    this.statusMap = {};
    this.actorMap = {};
    this.actionMap = {};
    this.currentAlphabet = 'A';
  }

  constructMermaidString() {
    this.clearExistingMap();
    let mermaidString = 'graph TD\n';
    this.wfjson.forEach((status: any) => {
      const startStatus = status.data.name;
      if (status.children) {
        status.children.forEach((actor: any) => {
          if (actor.data.type === 'actor') {
            const startingStatus = startStatus;
            if (actor.data.actor) {
              const currentActorsArray = JSON.parse(actor.data.actor);
              const currentActor: string[] = [];
              currentActorsArray.forEach((currActObj) => {
                if (currActObj.type === 'field' && currActObj.value) {
                  currActObj.value.split(' , ').forEach((e) => {
                    if (e) {
                      currentActor.push(this.fieldIdMap[e]);
                    }
                  });
                } else if (currActObj.type === 'role' && currActObj.value) {
                  currActObj.value.split(',').forEach((e) => {
                    const role = this.roles.find((elem) => elem.sid === e);
                    if (role) {
                      currentActor.push(role.shortName);
                    }
                  });
                }
              });
              if (actor.children) {
                actor.children.forEach((action) => {
                  const currentAction = action.data.action;
                  let nextStatus;
                  if(this.renderInsideModernizeMenu){
                    nextStatus = this.status ? this.status[action.data.statusTo] : null;
                  }else {
                    nextStatus =  this.appMetaInfo ? this.appMetaInfo[action.data.statusTo] : null;
                  }
                  if (!nextStatus) {
                    return;
                  }
                  this.updateStatusMap(startingStatus);
                  this.updateStatusMap(nextStatus);
                  this.updateActionMap(currentAction);
                  let actorString = currentActor.join(' , ');
                  actorString = `User : ${actorString} <br/> Action : ${currentAction}`;
                  mermaidString += `${this.getStatusString(startingStatus)} --> |${actorString}| ${this.getStatusString(nextStatus)}\n`;
                });
              }
            }
          }
        });
      }
    });
    mermaidString += 'classDef startNode fill:#e8f5e9,stroke:#81c784,stroke-width:1px\nclassDef endNode fill:#ffebee,stroke:#e57373,stroke-width:1px\n';
    this.workflowString = mermaidString;
  }

  formQBCondition(query, queryString, condition?: string, lastElem?: boolean) {
    const conditions = cloneDeep(query);
    if (
      Object.prototype.hasOwnProperty.call(conditions, 'conditon') ||
      Object.prototype.hasOwnProperty.call(conditions, 'rules')
    ) {
      const rules = conditions.rules;
      const condition = conditions.condition;
      const lastIndex = rules.length - 1;
      rules.forEach((e, i) => {
        this.formQBCondition(e, queryString, condition, i === lastIndex);
      });
    } else {
      const e = conditions;
      const rule: any = {};
      rule.field = e.field; // e.lhs?.fieldId;
      rule.operator = e.operator;
      rule.value = e.value; //e.rhs?.fieldId;
      queryString += `${rule.field} ${rule.operator} ${rule.value} ${!lastElem ? condition : ''}`;
    }
  }

  updateActionMap(action) {
    if (!Object.prototype.hasOwnProperty.call(this.actionMap, action)) {
      this.actionMap[action] = this.getNextAlphabet();
    }
  }
  updateStatusMap(status) {
    if (!Object.prototype.hasOwnProperty.call(this.statusMap, status)) {
      this.statusMap[status] = this.getNextAlphabet();
    }
  }
  async drawWFDiagram() {
    try {
      if (!this.mermaidDiv) {
        return;
      }
      // Don't render until we have actual workflow data
      if (!this.wfjson || this.wfjson.length === 0) {
        return;
      }
      if(!this.renderVisualizerOnly){
        this.fitToScreen();
      }
      this.constructMermaidString();
      this.zoomElement = Panzoom(this.mermaidDiv.nativeElement);
      mermaid.init(this.mermaidDiv.nativeElement);
      await this.drawDiagram();
    } catch (e) {
      console.error(e);
    }
  }

  async drawDiagram() {
    const { svg } = await mermaid.render('diagram', this.workflowString);
    this.mermaidDiv.nativeElement.innerHTML = svg;
    this.isMermaidRendered.emit();
  }

  insertSvg = (svgCode, bindFunctions) => {
    this.mermaidDiv.nativeElement.innerHTML = svgCode;
  };

  callback(svgCode: any) {
    return svgCode;
  }

  zoomIn() {
    this.zoomElement?.zoomTo(0, 0, 2);
  }
  zoomOut() {
    this.zoomElement?.zoomTo(0, 0, 1);
    this.zoomElement?.smoothZoom(0, 0, 0.5);
  }
  closeVisualizer() {
    this.closeVis.emit();
  }
  fitToScreen(fromInit?) {
    if (!fromInit) {
      if ($('.mermaid-wrapper').hasClass('mermaid-fullscreen')) {
        $('.mermaid-wrapper').removeClass('mermaid-fullscreen');
        $('.mermaid-wrapper').addClass('reset-mermaid-fullscreen');
        $('.mermaid-wrapper .icon-expand').removeClass('d-none');
        $('.mermaid-wrapper .icon-collapse').addClass('d-none');
      } else {
        $('.mermaid-wrapper').addClass('mermaid-fullscreen');
        $('.mermaid-wrapper').removeClass('reset-mermaid-fullscreen');
        $('.mermaid-wrapper .icon-collapse').removeClass('d-none');
        $('.mermaid-wrapper .icon-expand').addClass('d-none');
      }
    }
    this.zoomElement?.moveTo(0, 0);
    this.zoomElement?.zoomAbs(0, 0, 1);
  }

  getStatusString(status) {
    if (this.getStatusId(status) === this.initialStatus) {
      return `${this.statusMap[status]}(${status}):::startNode`;
    } else if (this.endStatus.includes(this.getStatusId(status))){
      return `${this.statusMap[status]}([${status}]):::endNode`;
    }
    return `${this.statusMap[status]}[${status}]`;
  }
  getStatusId(status) {
    let statusId = '';
    Object.keys(this.status).forEach(key => {
      if (this.status[key] === status) {
        statusId = key;
      }
    });
    return statusId;
  }
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setPageHeight();
  }

  setPageHeight() {
    const headerHt = $('#vbHeader').outerHeight() ?? 0;
    let pageHeaderHt = $('#vbPageHeader').outerHeight();
    pageHeaderHt = pageHeaderHt ? pageHeaderHt : 0;
    const windowHt = $(window).height() ?? 0;

    const pageHeight = windowHt - (headerHt + pageHeaderHt + 40);
    this.mermaidDiv.nativeElement.style.height = `${pageHeight - 70}px`;
  }

  ngOnInit(): void {

    if(!this.renderInsideModernizeMenu){
      const idMapApi = ApiConstants.getApplicationDataModelMetaInfo;
      this.store.dispatch(
        getIdMapping({
          getIdMappingApiConstants: idMapApi
        })
      );
    this.idsSub = this.store.select(allIdMaps).subscribe((res: any) => {
      if (res) {
        let fieldsMap;
        if (typeof res.fieldsMap === 'string') {
          this.fieldIdMap = JSON.parse(res.fieldsMap);
          this.tableIdMap = JSON.parse(res.tablesMap);
        } else {
          this.fieldIdMap = res.fieldsMap;
          this.tableIdMap = res.tablesMap;
        }
        // this.drawWFDiagram();
      }
    });
    }
    mermaid.initialize({
      theme: 'base'
    });
    if (!this.isFullScreen && !this.renderVisualizerOnly) {
      this.fitToScreen();
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if(this.renderInsideModernizeMenu && this.renderVisualizerOnly){
      this.previousWfjsonString = JSON.stringify(this.wfjson);
    }
    this.drawWFDiagram();
  }

  ngOnDestroy(): void {
    $(this.mermaidDiv.nativeElement).remove();
    this.zoomElement?.dispose();
    setTimeout(() => {
      if (document.querySelector('#diagram')) {
        document.querySelector('#diagram')?.remove();
      }
    });
    this.idsSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only redraw after initial render is complete and when wfjson actually changes
    if (this.isViewInitialized && changes['wfjson']) {
      if(this.renderVisualizerOnly){
        const currentWfjsonString = JSON.stringify(this.wfjson);
        if (currentWfjsonString !== this.previousWfjsonString) {
          this.previousWfjsonString = currentWfjsonString;
          this.drawWFDiagram();
        }
      } else {
        this.drawWFDiagram();
      }
    }
  }
}
